/**
 * evalAuoPortal — the portal seam that lets the RIGHT pane ("AUO disposition"
 * tab) host the EXACT same `AuoReviewPanel` instance the left-pane marking
 * surface (StaffTechnicalPanel) owns, instead of a look-alike re-implementation.
 *
 * WHY A PORTAL (not a re-render / not a data adapter). AuoReviewPanel is fully
 * wired inside StaffTechnicalPanel (real marks, real palette, real session
 * isAdmin, and the authoritative onConfirmAuo → fixtures[].status → report path).
 * Re-implementing it in the right pane drifts and drops controls. Instead, when
 * the panel is in workspace mode it renders its live `AuoReviewPanel` subtree via
 * `createPortal` into a target <div> that physically lives in the right pane's
 * AUO tab. Same component, same props, same state, zero adapter — only the DOM
 * mount point moves. The map stays in the left pane; the review card appears on
 * the right.
 *
 * SEAM SHAPE (mirrors evalDispositionBridge): the right pane REGISTERS its target
 * DOM node here; the panel SUBSCRIBES and portals into it. A module-level current
 * value + immediate replay makes it order-independent (either side can mount
 * first). No React, no Firebase.
 */

let currentHost: HTMLElement | null = null;
const listeners = new Set<(host: HTMLElement | null) => void>();

/** Right pane: publish (or clear with null) the DOM node that should host the
 *  real AuoReviewPanel. Call with the node on mount, null on unmount/tab-change. */
export function setAuoPortalHost(host: HTMLElement | null): void {
  currentHost = host;
  for (const cb of listeners) cb(currentHost);
}

/** Panel side: subscribe to the current portal host. Replays the current value
 *  immediately so subscribe order never matters. Returns an unsubscribe fn. */
export function subscribeAuoPortalHost(
  cb: (host: HTMLElement | null) => void,
): () => void {
  listeners.add(cb);
  cb(currentHost);
  return () => {
    listeners.delete(cb);
  };
}
